import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AccessTimeOutlined, ChevronRightOutlined, VisibilityOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import DashboardCard, { CardHeader, StatusPill, OpenLink, ColHeader, PrimaryButton, EmptyState } from '../DashboardCard/DashboardCard';
import QuickLookDrawer from '../../QuickLookDrawer/QuickLookDrawer';

function getSubmissionType(dayCount) {
  if (dayCount < 8)  return 'weekly';
  if (dayCount > 16) return 'monthly';
  return 'bimonthly';
}

function formatPeriod(ts) {
  const days = ts.TimesheetDetails?.[0]?.TimesheetHoursByDates
            ?? ts.TimesheetSections?.[0]?.TimesheetDetails?.[0]?.TimesheetHoursByDates;

  if (days?.length) {
    const start     = dayjs(days[0].Day);
    const end       = dayjs(days.at(-1).Day);
    const dayCount  = days.length;
    const subType   = getSubmissionType(dayCount);
    const sameMonth = start.isSame(end, 'month');

    if (subType === 'monthly') return start.format('MMMM YYYY');

    if (subType === 'bimonthly') {
      if (sameMonth) {
        return `${start.format('MMMM')} (${start.format('D')} – ${end.format('D')})`;
      }
      return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
    }

    if (sameMonth) {
      return `${start.format('D')} – ${end.format('D MMM YYYY')}`;
    }
    return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }

  if (ts.PeriodLabel?.trim()) return ts.PeriodLabel;

  const startRaw = ts.StartDate ?? ts.PeriodStartDate ?? ts.FromDate;
  const endRaw   = ts.EndDate   ?? ts.PeriodEndDate   ?? ts.ToDate;
  const freq     = (ts.Frequency ?? ts.SubmissionFrequency ?? '').toLowerCase();

  if (startRaw) {
    const s = dayjs(startRaw);
    const e = endRaw ? dayjs(endRaw) : null;

    if (freq.includes('month') && !freq.includes('bi')) return s.format('MMMM YYYY');
    if (!e) return s.format('D MMM YYYY');
    if (s.isSame(e, 'month')) return `${s.format('MMMM')} (${s.format('D')} – ${e.format('D')})`;
    return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
  }

  return '—';
}

function freqLabel(ts) {
  const days = ts.TimesheetDetails?.[0]?.TimesheetHoursByDates
            ?? ts.TimesheetSections?.[0]?.TimesheetDetails?.[0]?.TimesheetHoursByDates;

  if (days?.length) {
    const t = getSubmissionType(days.length);
    if (t === 'monthly')   return 'Monthly';
    if (t === 'bimonthly') return 'Bi-monthly';
    return 'Weekly';
  }

  const freq = (ts.Frequency ?? ts.SubmissionFrequency ?? '').toLowerCase();
  if (!freq) return '';
  if (freq.includes('monthly') && !freq.includes('bi')) return 'Monthly';
  if (freq.includes('bimonthly') || freq.includes('bi-monthly'))  return 'Bi-monthly';
  if (freq.includes('biweekly')  || freq.includes('bi-weekly'))   return 'Bi-weekly';
  if (freq.includes('weekly'))   return 'Weekly';
  return ts.Frequency ?? '';
}

function initials(first, last) {
  return `${(first ?? '')[0] ?? ''}${(last ?? '')[0] ?? ''}`.toUpperCase() || '—';
}

const AVATAR_COLORS = ['#0094B3', '#7C3AED', '#059669', '#DB2777', '#D97706', '#2563EB'];
function avatarColor(seed) {
  const str = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash];
}

function SkeletonRow() {
  return (
    <div className="flex items-center px-5 py-4 border-b border-[#E2E8ED] gap-3 animate-pulse">
      <div style={{ flex: 2 }}>
        <div className="h-3.5 bg-[#E2E8ED] rounded w-36 mb-1.5" />
        <div className="h-3 bg-[#E2E8ED] rounded w-16" />
      </div>
      <div className="h-3.5 bg-[#E2E8ED] rounded w-24 hidden md:block" style={{ flex: 1.4 }} />
      <div className="h-5 bg-[#E2E8ED] rounded-full w-20" style={{ flex: 1 }} />
      <div className="h-3.5 bg-[#E2E8ED] rounded w-10 hidden md:block" style={{ flex: 0.6 }} />
    </div>
  );
}

function FilterTag({ label, count, colorCls, dotCls, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 text-[13px] font-semibold ${colorCls} bg-transparent border-none cursor-pointer p-0 hover:opacity-70 transition-opacity`}
    >
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      {count} {label}
    </button>
  );
}

function ValidationBanner({ isValidAdmin, isValidPlacement, isApprover }) {
  if (isValidAdmin === undefined) return null;
  if (!isValidAdmin && !isApprover) return (
    <div className="flex items-center gap-2.5 mx-5 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-[8px]">
      <span className="text-amber-500 shrink-0">⚠</span>
      <p className="text-[13px] text-amber-700 m-0">Timesheet settings not configured — contact your administrator.</p>
    </div>
  );
  if (!isValidPlacement) return (
    <div className="flex items-center gap-2.5 mx-5 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-[8px]">
      <span className="text-amber-500 shrink-0">⚠</span>
      <p className="text-[13px] text-amber-700 m-0">No active placement — timesheets are currently unavailable.</p>
    </div>
  );
  return null;
}

export default function TimesheetsCard({ animDelay = 0, onApprove, onReject }) {
  const [quickLookItem, setQuickLookItem] = useState(null);
  const navigate = useNavigate();

  const timesheetsList           = useSelector((s) => s.timesheets?.data);
  const timesheetsPending        = useSelector((s) => s.timesheets?.pending);
  const isApprover               = useSelector((s) => s.accessToParts?.tAndEApprover);
  const timesheetValidStatus     = useSelector((s) => s?.timesheet?.validate?.timesheet?.IsValidAdminSettings);
  const timesheetPlacementStatus = useSelector((s) => s?.timesheet?.validate?.timesheet?.IsValidPlacementStatus);
  const approverCounts           = useSelector((s) => s.timesheet?.tandecounts);
  const contractorCounts         = useSelector((s) => s.timesheet?.contractortandecounts?.Timesheet);

  const approvedCount = contractorCounts?.ApprovedCount ?? 0;
  const rejectedCount = contractorCounts?.RejectedCount ?? 0;
  const pendingCount  = isApprover
    ? (approverCounts?.PendingCount ?? 0)
    : (contractorCounts?.SubmittedCount ?? 0);

  const isLoading = timesheetsPending || timesheetsList === null;
  const preview   = (timesheetsList ?? []).slice(0, 3);

  const cols = isApprover
    ? [
        { label: 'Period',      flex: 2   },
        { label: 'Contractor',  flex: 1.4 },
        { label: 'Status',      flex: 1   },
        { label: '',            flex: 0.6 },
      ]
    : [
        { label: 'Period',  flex: 2   },
        { label: 'Client',  flex: 1.4 },
        { label: 'Status',  flex: 1   },
        { label: '',        flex: 0.6 },
      ];

  return (
    <DashboardCard noPadding animDelay={animDelay}>
      <CardHeader
        icon={<AccessTimeOutlined />}
        title="Timesheets"
        action={
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <button
                onClick={() => navigate('/dashboard/timesheets?status=Submitted')}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#D97706] bg-[#FEF3C7] px-2.5 py-1 rounded-full border-none cursor-pointer hover:bg-[#FDE68A] transition-colors"
              >
                <span className="w-[5px] h-[5px] rounded-full bg-[#F59E0B] shrink-0" />
                {pendingCount} <span className='md:flex hidden'>pending</span>
              </button>
            )}
            <span
              onClick={() => navigate('/dashboard/timesheets')}
              className="text-[14px] text-[#0094B3] font-medium cursor-pointer hover:text-[#0081A0] transition-colors"
            >
              View all →
            </span>
          </div>
        }
      />

      {isApprover && pendingCount > 0 && (
        <p className="flex items-center gap-1.5 md:hidden text-[12px] text-[#9AA3AE] px-5 pb-3 -mt-2 m-0">
          <span aria-hidden>👆</span> Tap a row to review
        </p>
      )}

      <ValidationBanner
        isValidAdmin={timesheetValidStatus}
        isValidPlacement={timesheetPlacementStatus}
        isApprover={isApprover}
      />

      {!isApprover && (
        <div className="hidden md:flex items-center gap-5 px-5 pb-4">
          <FilterTag
            label="Approved" count={approvedCount}
            colorCls="text-[#059669]" dotCls="bg-[#10B981]"
            onClick={() => navigate('/dashboard/timesheets?status=Approved')}
          />
          <FilterTag
            label="Rejected" count={rejectedCount}
            colorCls="text-[#DC2626]" dotCls="bg-[#EF4444]"
            onClick={() => navigate('/dashboard/timesheets?status=Rejected')}
          />
          <FilterTag
            label="Pending" count={pendingCount}
            colorCls="text-[#D97706]" dotCls="bg-[#F59E0B]"
            onClick={() => navigate('/dashboard/timesheets?status=Submitted')}
          />
        </div>
      )}

      <div className="hidden md:block">
        <ColHeader cols={cols} />
      </div>

      {isLoading ? (
        [0, 1, 2].map((i) => <SkeletonRow key={i} />)
      ) : preview.length === 0 ? (
        <EmptyState message="No timesheets yet" />
      ) : (
        preview.map((ts) => {
          const contractorName = `${ts.ContractorFirstName ?? ''} ${ts.ContractorLastName ?? ''}`.trim();
          return (
            <div key={ts.Id}>
              {/* Mobile row */}
              <div
                className="flex md:hidden items-center gap-3 px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 active:bg-[#F8FAFB] transition-colors cursor-pointer"
                onClick={() => navigate(`/dashboard/timesheet/${ts.Id}`)}
              >
                {isApprover ? (
                  <span
                    className="flex items-center justify-center w-9 h-9 rounded-full text-white text-[12px] font-bold shrink-0"
                    style={{ backgroundColor: avatarColor(ts.Id ?? contractorName) }}
                  >
                    {initials(ts.ContractorFirstName, ts.ContractorLastName)}
                  </span>
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-[#022D41] leading-tight truncate">
                    {isApprover ? (contractorName || ts.ClientName) : formatPeriod(ts)}
                  </div>
                  <div className="text-[12.5px] text-[#9AA3AE] mt-0.5 truncate">
                    {isApprover ? formatPeriod(ts) : ts.ClientName}
                    {ts.TSNumber || ts.Reference ? ` · ${ts.TSNumber ?? ts.Reference}` : ''}
                  </div>
                </div>
                <StatusPill status={ts.Status} />
                {isApprover && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setQuickLookItem(ts); }}
                    aria-label="Quick look"
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F0F4F7] border-none cursor-pointer shrink-0 hover:bg-[#E2E8ED] transition-colors"
                  >
                    <VisibilityOutlined sx={{ fontSize: 15, color: '#0094B3' }} />
                  </button>
                )}
                <ChevronRightOutlined sx={{ fontSize: 18, color: '#C4CDD5', flexShrink: 0 }} />
              </div>

              {/* Desktop row */}
              <div
                className="hidden md:flex items-center px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 hover:bg-[#F8FAFB] transition-colors cursor-pointer group"
                onClick={() => navigate(`/dashboard/timesheet/${ts.Id}`)}
              >
                <div style={{ flex: 2 }}>
                  <div className="text-[15px] font-semibold text-[#022D41] group-hover:text-[#0094B3] transition-colors leading-tight">
                    {formatPeriod(ts)}
                  </div>
                  <div className="text-[12px] text-[#9AA3AE] mt-0.5">{freqLabel(ts)}</div>
                </div>

                <div className="text-[14px] text-[#6B7A85] truncate" style={{ flex: 1.4 }}>
                  {isApprover ? (contractorName || ts.ClientName) : ts.ClientName}
                </div>

                <div style={{ flex: 1 }}>
                  <StatusPill status={ts.Status} />
                </div>

                <div style={{ flex: 0.6 }}>
                  <OpenLink onClick={() => navigate(`/dashboard/timesheet/${ts.Id}`)} />
                </div>
              </div>
            </div>
          );
        })
      )}

      {!isApprover && (
        <div className="mt-auto">
          <PrimaryButton
            onClick={() => navigate('/dashboard/create-timesheet')}
            variant={timesheetValidStatus && timesheetPlacementStatus ? 'dark' : 'disabled'}
          >
            + New Timesheet
          </PrimaryButton>
        </div>
      )}

      <QuickLookDrawer
        open={!!quickLookItem}
        item={quickLookItem}
        type="timesheet"
        onClose={() => setQuickLookItem(null)}
        onApprove={onApprove}
        onReject={onReject}
      />
    </DashboardCard>
  );
}